import React, { useCallback, useState, useEffect, useRef } from "react";
import { useRADDispatch } from "./contexts/RADDispatchContext.jsx";
import { RADEventProvider } from "./contexts/RADEventContext.jsx";
import { RADSelectionProvider } from "./contexts/RADSelectionContext.jsx";
import RADLayout from "../RADDrawing/RADLayout.jsx";
import RADEditor from "../RADEditor/RADEditor.jsx";
import {
  queryTargetConnections,
  TARGET_BLOCK_RULES,
  TARGET_BLOCK_RULE_TEXT,
  getAncestorStates,
  getMergeStates,
} from "@cadenza/rad-domain";
import { useMessages } from "@cadenza/messages";
import { useOutsideEvent } from "@cadenza/ui-hooks";

const RADHandler = () => {
  const { state: rad, addInteraction, addLoop, addMerge } = useRADDispatch();

  const [selectedComponent, setSelectedComponent] = useState(null);
  const [isConnecting, setIsConnecting] = useState(false);
  const [isLooping, setIsLooping] = useState(false);
  const [isMerging, setIsMerging] = useState(false);
  const [showTargets, setShowTargets] = useState(false);
  const { success, error } = useMessages();
  // Ref for the rad-container to detect outside events
  const radContainerRef = useRef(null);

  // Function to cancel connecting/looping modes
  const cancelConnectingMode = useCallback(() => {
    if (isConnecting) {
      console.log("Canceling connecting mode");
      setIsConnecting(null);
      success("Connection mode canceled");
    }
    if (isLooping) {
      console.log("Canceling looping mode");
      setIsLooping(null);
      success("Loop mode canceled");
    }
    if (isMerging) {
      console.log("Canceling merging mode");
      setIsMerging(null);
      success("Merge mode canceled");
    }
  }, [isConnecting, isLooping, isMerging, success]);

  // Handle ESC key to cancel modes
  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === "Escape" && (isConnecting || isLooping || isMerging)) {
        event.preventDefault();
        event.stopPropagation();
        cancelConnectingMode();
      }
    };

    document.addEventListener("keydown", handleEscape, true);
    return () => {
      document.removeEventListener("keydown", handleEscape, true);
    };
  }, [cancelConnectingMode, isConnecting, isLooping, isMerging]);

  // Handle mouse leaving the rad-container area to cancel connecting mode
  useOutsideEvent(
    radContainerRef,
    isConnecting || isLooping || isMerging, // Only active when in connecting, looping, or merging mode
    cancelConnectingMode,
    {
      events: ["pointermove"],
      throttleMs: 200,
    },
  );

  const handleClick = useCallback(
    (d) => {
      if (!d || !d.id) return;
      if (isConnecting) {
        console.log("Connecting", isConnecting, d);
        if (!isConnecting.source) {
          // First click - set source
          setIsConnecting({ source: d });
        } else if (
          isConnecting.targets &&
          isConnecting.targets.includes(d.id)
        ) {
          // Second click - set selected target
          // This will cascade to the effect that creates the interaction
          setIsConnecting({ ...isConnecting, selected: d });
        } else {
          const blockedItem = isConnecting?.targetQuery?.blocked?.find(
            (item) => item.id === d.id,
          );
          const reason = blockedItem?.reason;
          const ruleId =
            blockedItem?.ruleId ||
            (reason && TARGET_BLOCK_RULES[reason]?.id) ||
            "TRG-000";
          const message =
            TARGET_BLOCK_RULE_TEXT[ruleId] || TARGET_BLOCK_RULE_TEXT["TRG-000"];
          error(message);
          setIsConnecting(null);
        }
        return;
      }
      if (isLooping) {
        console.log("Looping", d);
        if (!["state"].includes(d.class)) {
          console.error("Can only loop to a state. Resetting loop.");
          setIsLooping(null);
          return;
        }
        setIsLooping([...isLooping, d]);
        return;
      }
      if (isMerging) {
        if (!["state"].includes(d.class)) {
          console.error("Can only merge to a state. Resetting merge.");
          setIsMerging(null);
          return;
        }
        setIsMerging([...isMerging, d]);
        return;
      }
      setSelectedComponent(d);
    },
    [isConnecting, isLooping, isMerging, error],
  );

  const handleBackgroundClick = useCallback(() => {
    setSelectedComponent(null);
    setIsConnecting(null);
    setIsLooping(null);
    setIsMerging(null);
  }, []);

  // Reset selection and connection state when RAD ID changes
  useEffect(() => {
    setSelectedComponent(null);
    setIsConnecting(null);
    setIsLooping(null);
    setIsMerging(null);
  }, [rad?.id]);

  useEffect(() => {
    // isConnecting is an object with named properties to track connection creation progress
    // { source, targets, selected } - allows the UI to highlight targets and track progress
    if (!isConnecting) return;

    if (isConnecting.source && !isConnecting.targets) {
      const targetQuery = queryTargetConnections(rad)(isConnecting.source);
      if (
        typeof window !== "undefined" &&
        window.__RAD_TARGET_TRACE__ === true
      ) {
        console.log("RAD target query trace", {
          source: isConnecting.source,
          trace: targetQuery.trace ?? null,
          allowedIds: targetQuery.allowedIds,
          blocked: targetQuery.blocked,
          warnings: targetQuery.warnings,
        });
      }
      setIsConnecting({
        ...isConnecting,
        targets: targetQuery.allowedIds,
        targetQuery,
      });
    }

    if (isConnecting.source && isConnecting.selected) {
      console.log(
        "Connecting",
        isConnecting.source,
        "TO",
        isConnecting.selected,
      );
      const result = addInteraction({
        source: isConnecting.source,
        selected: isConnecting.selected,
      });
      if (result?.success) {
        success(result.message);
      } else {
        error(result?.message || "Failed to add connection");
      }
      setIsConnecting(null);
    }
  }, [isConnecting, rad, addInteraction, success, error]);

  useEffect(() => {
    if (!isLooping) return;
    if (isLooping.length === 1) {
      console.log("Selected loop start:", isLooping[0]);
      const targets = getAncestorStates(rad)(isLooping[0].id);
      setIsLooping([...isLooping, targets]);
      console.log("Loop targets:", targets);
    }
    if (isLooping.length === 3) {
      console.log("Looping", isLooping);
      addLoop(isLooping);
      setIsLooping(null);
      setSelectedComponent(null);
    }
  }, [isLooping, addLoop, rad]);

  useEffect(() => {
    if (!isMerging) return;
    if (isMerging.length === 1) {
      const targets = getMergeStates(rad)(isMerging[0]);
      setIsMerging([...isMerging, targets]);
    }
    if (isMerging.length === 3) {
      addMerge([isMerging[0], isMerging[2]]); // Not interested in targets here
      setIsMerging(null);
      setSelectedComponent(null);
    }
  }, [isMerging, addMerge, rad]);

  return (
    <div id="rad-handler">
      <RADSelectionProvider
        value={{
          selectedComponent,
          setSelectedComponent,
          isConnecting,
          setIsConnecting,
          isLooping,
          setIsLooping,
          isMerging,
          setIsMerging,
          showTargets,
          setShowTargets,
        }}
      >
        {rad?.id && (
          <div className="rad-container" ref={radContainerRef}>
            <RADEventProvider
              onClick={handleClick}
              onBackgroundClick={handleBackgroundClick}
            >
              <RADLayout />
            </RADEventProvider>
            <RADEditor />
          </div>
        )}
      </RADSelectionProvider>
    </div>
  );
};

export default RADHandler;
